import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import './App.css';
import { InstallBanner } from './components/InstallBanner';
import { AppRoutes } from './routes/AppRoutes';
import { InquiryMaterialsGate } from './components/inquiry/InquiryMaterialsGate';
import { canStoreJob } from './lib/storeJobPermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// In-memory token storage (not accessible via sessionStorage/XSS)
let memoryToken = null;

export const getToken = () => memoryToken;
export const setMemoryToken = (t) => { memoryToken = t; };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = useCallback(async (username, password) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { username, password }, { withCredentials: true });
      const { token: newToken, user: userData } = res.data;
      memoryToken = newToken;
      setToken(newToken);
      setUser(userData);
      toast.success('Login successful');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (err) { console.error('Logout request failed:', err); }
    memoryToken = null;
    setToken(null);
    setUser(null);
    toast.info('Logged out');
  }, []);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          ...(memoryToken ? { headers: { Authorization: `Bearer ${memoryToken}` } } : {}),
        });
        setUser(res.data);
      } catch {
        memoryToken = null;
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };
    verifyToken();
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch (error) {
      if (error.response?.status === 401) setUser(null);
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    window.addEventListener('focus', refreshUser);
    const interceptor = axios.interceptors.response.use(response => response, error => {
      if (error.response?.status === 403 && !error.config?.url?.endsWith('/auth/me')) refreshUser();
      return Promise.reject(error);
    });
    return () => {
      window.removeEventListener('focus', refreshUser);
      axios.interceptors.response.eject(interceptor);
    };
  }, [user, refreshUser]);

  const value = { user, token, login, logout, loading, refreshUser, isAuthenticated: !!user };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route
const ProtectedRoute = ({ children, roles, section }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="loader" /></div>;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user?.role === 'store_manager') {
    const isOrders = location.pathname === '/confirmed-orders';
    const isJob = /^\/jobs\/[^/]+$/.test(location.pathname);
    if (!isOrders && (!isJob || !canStoreJob(user))) return <Navigate to="/confirmed-orders" replace />;
  }
  if (roles && !roles.includes(user?.role)) {
    return <Navigate to="/home" replace />;
  }
  if (section && user?.allowedSections && !user.allowedSections.includes(section)) {
    return <Navigate to="/home" replace />;
  }
  return children;
};

// Axios interceptor - only set up once
let interceptorsSetup = false;

// Iter70 — global "no silent fails" guard.
// Dedupe identical error toasts fired within 300 ms so the global response
// interceptor (below) doesn't double-toast when a local catch also calls
// toast.error with the same detail string.
const _recentToasts = new Map();
const _origToastError = toast.error.bind(toast);
toast.error = (msg, opts) => {
  const key = typeof msg === 'string' ? msg : JSON.stringify(msg);
  const now = Date.now();
  // Lazy TTL prune — keep the Map small AND ensure FIFO eviction never
  // removes a key still inside its 300 ms dedupe window.
  for (const [k, ts] of _recentToasts) {
    if (now - ts > 300) _recentToasts.delete(k);
    else break; // Map preserves insertion order; once we hit a live entry, the rest are live too
  }
  const prev = _recentToasts.get(key);
  if (prev && now - prev < 300) return prev;
  _recentToasts.set(key, now);
  return _origToastError(msg, opts);
};

const setupInterceptors = () => {
  if (interceptorsSetup) return;
  interceptorsSetup = true;

  axios.interceptors.request.use(config => {
    if (memoryToken) {
      config.headers.Authorization = `Bearer ${memoryToken}`;
    }
    config.withCredentials = true;
    return config;
  });

  axios.interceptors.response.use(
    response => response,
    error => {
      const requestUrl = error.config?.url || '';
      const isAuthCheck = requestUrl.includes('/auth/me');
      if (error.response?.status === 401 && !isAuthCheck && !window.location.pathname.includes('/login')) {
        memoryToken = null;
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Iter70 — auto-toast on 4xx/5xx for write methods so silent-failure
      // bugs (see iter69 drawing upload) can't recur. Opt-out via
      // axios.post(url, body, { skipGlobalErrorToast: true }).
      // Local catches that already toast the same detail are deduped by the
      // wrapper above (300 ms window).
      const method = (error.config?.method || '').toUpperCase();
      const status = error.response?.status;
      const writeMethod = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
      const skip = error.config?.skipGlobalErrorToast;
      if (writeMethod && status >= 400 && status !== 401 && !skip) {
        // Defer via setTimeout(0) so any local .catch() (which runs as a
        // microtask immediately after this interceptor returns) gets a
        // chance to fire its toast.error FIRST and register in
        // _recentToasts. The global toast then becomes a no-op via the
        // dedupe wrapper — local custom messages win.
        setTimeout(() => {
          const detail = error.response?.data?.detail
            || error.response?.data?.message
            || error.message
            || 'Request failed';
          toast.error(detail);
        }, 0);
      }

      return Promise.reject(error);
    }
  );
};

setupInterceptors();

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <InstallBanner />
        <AppRoutes ProtectedRoute={ProtectedRoute} />
        <InquiryMaterialsGate />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
